import PersonIcon from '@mui/icons-material/Person';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { CompanyAbout, ContactAbout, ContactDetails, DrawerTabs, Qualifications } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import AboutCompany from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/about-company';
import AboutContact from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/about-contact';
import AssessmentOverview from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/assessment-overview';
import ContactCard from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-card';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Assessment } from 'types/analysis-result.types';
import { ProfileValues } from 'types/profile.types';

interface TabProps {
  label: string;
  value: DrawerTabs;
  active: boolean;

  handleActiveTabChange(value: DrawerTabs): () => void;
}

const Tab: React.FC<TabProps> = ({ label, value, active, handleActiveTabChange }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <ButtonBase className={cx(classes.tab, active ? 'active' : null)} onClick={handleActiveTabChange(value)} disableRipple={true}>
      <Typography className={classes.tabText}>{t(label)}</Typography>
    </ButtonBase>
  );
};

interface Props {
  loading: boolean;
  aiJudgement: string | undefined;
  aiJudgementExplanation: string | undefined;
  assessment: Assessment | undefined;
  percentiles: ProfileValues | undefined;
  contactDetails: ContactDetails | null;
  aboutContact: ContactAbout | null;
  aboutCompany: CompanyAbout | null;
}

const ContactDetailView: React.FC<Props> = ({
  loading,
  aiJudgement,
  aiJudgementExplanation,
  assessment,
  percentiles,
  contactDetails,
  aboutContact,
  aboutCompany,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [activeContactsTab, setActiveContactsTab] = useState(DrawerTabs.ASSESSMENT_OVERVIEW);

  const handleActiveContactsTabChange = (value: DrawerTabs) => {
    return () => {
      setActiveContactsTab(value);
    };
  };

  const qualificationToText = () => {
    switch (aiJudgement as Qualifications) {
      case Qualifications.A:
        return t('topCandidate');
      case Qualifications.B:
        return t('strongContender');
      case Qualifications.C:
        return t('backupCandidate');
      default:
        return '';
    }
  };

  const getTabContent = () => {
    switch (activeContactsTab) {
      case DrawerTabs.ASSESSMENT_OVERVIEW:
        return (
          <AssessmentOverview
            aiJudgementExplanation={aiJudgementExplanation}
            fullName={contactDetails?.fullName}
            assessment={assessment}
            percentiles={percentiles}
            qualificationToText={qualificationToText}
          />
        );
      case DrawerTabs.ABOUT_CONTACT:
        return <AboutContact contactDetails={contactDetails} aboutContact={aboutContact} qualificationToText={qualificationToText} />;
      case DrawerTabs.ABOUT_COMPANY:
        return <AboutCompany aboutCompany={aboutCompany} />;
      default:
        return <></>;
    }
  };

  return (
    <div className={classes.contactDetailView}>
      {loading ? (
        <Stack className={classes.loadingContainer} justifyContent={'center'} alignItems={'center'}>
          <PaiSpinningLoader size={'large'} />
        </Stack>
      ) : null}
      <Stack className={classes.sectionIndicator} direction={'row'} alignItems={'center'}>
        <div className={classes.line} />
        <PersonIcon className={classes.icon} />
        <Typography className={classes.sectionTitleText}>{t('personalityProfile')}</Typography>
      </Stack>
      <Stack className={classes.personalityProfile} gap={2}>
        {!_.isNil(assessment) ? (
          <div className={classes.drawerContent}>
            <ContactCard
              contactDetails={contactDetails}
              isActive={false}
              disableShadow={true}
              qualification={aiJudgement}
              qualificationToText={qualificationToText}
            />
            <div className={classes.tabsContainer}>
              <Stack className={classes.tabs} direction={'row'} alignItems={'flex-start'} gap={4}>
                {_.map(DrawerTabs, (tab) => (
                  <Tab key={tab} label={tab} value={tab} active={activeContactsTab === tab} handleActiveTabChange={handleActiveContactsTabChange} />
                ))}
              </Stack>
              <Stack className={classes.tabContent} gap={2}>
                {getTabContent()}
              </Stack>
            </div>
          </div>
        ) : (
          <Typography style={{ whiteSpace: 'pre-wrap' }}>
            {t('noAssessmentAvailable')}
          </Typography>
        )}
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactDetailView: {
    position: 'relative',
    background: theme.palette.backgrounds.main_new,
    padding: 'calc(32px + 32px + 56px) 64px 32px 64px',
    borderRadius: '10px',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#FFF',
    opacity: 0.8,
    zIndex: 90,
  },
  sectionIndicator: {
    position: 'absolute',
    left: 0,
    top: '32px',
  },
  line: {
    background: theme.palette.primary.main,
    height: '2px',
    width: '64px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(32),
    background: theme.palette.primary.main,
    borderRadius: '50%',
    color: '#FFF',
    padding: '4px',
  },
  sectionTitleText: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginLeft: '16px',
  },
  personalityProfile: {
    background: '#FFF',
    borderRadius: '10px',
  },
  drawerContent: {
    padding: '20px',
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    marginLeft: '-20px',
    marginRight: '-20px',
    marginTop: '20px',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  tab: {
    padding: '8px 0',
    color: theme.palette.greys.light,
    '&:hover': {
      borderBottom: `3px solid ${theme.palette.primary.main}`,
      paddingBottom: '6px',
    },
    '&.active': {
      color: theme.palette.primary.main,
      borderBottom: `3px solid ${theme.palette.primary.main}`,
      paddingBottom: '6px',
    },
  },
  tabText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  tabContent: {
    padding: '20px 20px 1px 20px',
    overflow: 'auto',
  },
}));

export default ContactDetailView;
